import { useState } from "react";
import { REVIEWS } from "../lib/brand";
import { useReveal } from "../hooks/useReveal";

const StarIcon = (props) => (
  <svg viewBox="0 0 24 24" fill="currentColor" {...props}>
    <path d="M12 2l3.09 6.26 6.91 1-5 4.87 1.18 6.88L12 17.77l-6.18 3.24L7 14.13 2 9.26l6.91-1L12 2z" />
  </svg>
);

const Quote = (props) => (
  <svg viewBox="0 0 48 36" fill="currentColor" aria-hidden {...props}>
    <path d="M0 36V21.6C0 9.6 6.2 2.4 18.6 0l3.2 6C14 8.4 10 12.4 10 18h7.6v18H0zm26.4 0V21.6c0-12 6.2-19.2 18.6-21.6l3.2 6c-7.8 2.4-11.8 6.4-11.8 12H44v18H26.4z" />
  </svg>
);

export const Reviews = () => {
  const headRef = useReveal();
  const cardsRef = useReveal();
  const [active, setActive] = useState(0);

  return (
    <section
      id="reviews"
      data-testid="reviews-section"
      className="py-24 md:py-40 border-b border-black"
    >
      <div className="max-w-[1480px] mx-auto px-6 md:px-12">
        {/* Header */}
        <div
          ref={headRef}
          className="reveal flex flex-col md:flex-row md:items-end md:justify-between gap-8 mb-14 md:mb-20"
        >
          <div>
            <div className="label-eyebrow mb-4">Kind words · Vol. VI</div>
            <h2 className="font-display text-[2.6rem] md:text-7xl leading-[0.95] tracking-[-0.015em]">
              What clients are <br /><span className="italic">saying.</span>
            </h2>
          </div>
          <div className="flex items-end gap-4 md:pb-3">
            <div className="flex items-center gap-1">
              {[0,1,2,3,4].map((i) => (
                <StarIcon key={i} className="w-5 h-5 text-black" />
              ))}
            </div>
            <div className="label-micro opacity-70">
              5.0 · based on verified rentals
            </div>
          </div>
        </div>

        {/* Featured review — large quote */}
        <div
          ref={cardsRef}
          className="reveal reveal-delay-2 grid grid-cols-12 gap-8 md:gap-16 items-start"
        >
          <div className="col-span-12 md:col-span-7 relative">
            <Quote className="w-12 h-9 md:w-16 md:h-12 mb-8 opacity-90" />
            <blockquote
              key={active}
              data-testid="review-featured-body"
              className="font-display text-2xl md:text-4xl leading-[1.25] tracking-[-0.01em]"
              style={{ animation: "hero-rise 0.7s cubic-bezier(0.22,1,0.36,1) both" }}
            >
              "{REVIEWS[active].body}"
            </blockquote>
            <div className="mt-10 flex items-center gap-5 pt-6 border-t border-black">
              <div>
                <div
                  data-testid="review-featured-name"
                  className="font-display text-xl md:text-2xl"
                >
                  {REVIEWS[active].name}
                </div>
                <div className="label-micro opacity-60 mt-1">
                  {REVIEWS[active].occasion}
                </div>
              </div>
              <div className="ml-auto flex items-center gap-1">
                {Array.from({ length: REVIEWS[active].rating }).map((_, i) => (
                  <StarIcon key={i} className="w-4 h-4 text-black" />
                ))}
              </div>
            </div>
          </div>

          {/* Side list of other reviews (clickable) */}
          <div className="col-span-12 md:col-span-4 md:col-start-9">
            <div className="label-eyebrow opacity-60 mb-5">More reviews</div>
            <ul className="border-t border-black">
              {REVIEWS.map((r, i) => (
                <li key={r.name} className="border-b border-black/15">
                  <button
                    type="button"
                    data-testid={`review-tab-${i}`}
                    onClick={() => setActive(i)}
                    onMouseEnter={() => setActive(i)}
                    className={`w-full text-left py-4 md:py-5 flex items-start justify-between gap-4 transition-all duration-400 ${
                      active === i ? "pl-3 md:pl-4 bg-black text-white" : "hover:pl-2 md:hover:pl-3"
                    }`}
                  >
                    <span>
                      <span className="font-display text-lg md:text-xl leading-tight block">
                        {r.name}
                      </span>
                      <span className={`label-micro mt-1 block ${active === i ? "opacity-70" : "opacity-55"}`}>
                        {r.occasion}
                      </span>
                    </span>
                    <span aria-hidden className="font-display text-sm mt-1.5">
                      {active === i ? "—" : "+"}
                    </span>
                  </button>
                </li>
              ))}
            </ul>

            <a
              href="#contact"
              data-testid="reviews-cta"
              className="btn-pill btn-pill-solid mt-10"
            >
              Share your experience
            </a>
          </div>
        </div>
      </div>
    </section>
  );
};
